import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { BankAccountDto } from 'api/bank-account';
import { ContextEnum } from 'api/files';
import { bankAccountsAPI, documentsAPI } from 'services/swagger-api';
import { useDocumentProcessDialog } from 'components/domain/Document/DocumentProcessDialog';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import {
  createRibUploadConfig,
  createAttachRibConfig
} from 'components/domain/Document/DocumentProcessDialog/configs';
import { AddIcon } from 'components/icons/Icons';
import { GenericLoading } from 'components/ui/Loader';
import Badge from 'components/ui/Badge';
import { GenericColors } from 'api/contract';
import { CheckIcon, Eye, Landmark } from 'lucide-react';
import { LightAction } from 'components';

interface BankAccountRibSelectorProps {
  personId: string;
  selectedBankAccountId?: string;
  onSelect: (bankAccountId: string) => void;
  /** Changer cette valeur force un rechargement de la liste des comptes. */
  refreshKey?: number;
}

// Extended interface to include storedFileId (will be in generated types after regeneration)
interface BankAccountWithStoredFile extends BankAccountDto {
  storedFileId?: string | null;
}

export const BankAccountRibSelector: React.FC<BankAccountRibSelectorProps> = ({
  personId,
  selectedBankAccountId,
  onSelect,
  refreshKey
}) => {
  const { t } = useTranslation([NsKey.Contractualization]);
  const { openDocumentProcess } = useDocumentProcessDialog();
  const { openDocumentViewer } = useDocumentViewerDialog();

  const [bankAccounts, setBankAccounts] = useState<BankAccountWithStoredFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch bank accounts on mount
  const fetchBankAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await bankAccountsAPI.getBankAccountsByPersonId({ personId });
      setBankAccounts(response.bankAccounts || []);
    } catch (error) {
      toast.error(t('CONTRACTUALIZATION.BANK_ACCOUNT.ERRORS.FETCH_FAILED'));
    } finally {
      setIsLoading(false);
    }
  }, [personId, t]);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts, refreshKey]);

  // Handle selection of a bank account
  const handleSelectAccount = useCallback((bankAccount: BankAccountWithStoredFile) => {
    // Case 1: Account has RIB attached - directly select
    if (bankAccount.storedFileId) {
      onSelect(bankAccount.id);
      return;
    }

    // Case 2: Account has no RIB - need to upload and attach
    openDocumentProcess({
      context: ContextEnum.Person,
      objectId: personId,
      config: createAttachRibConfig({
        bankAccountId: bankAccount.id,
        bankAccountIban: bankAccount.iban,
        onExtractIban: async (storedFileId) => {
          const result = await documentsAPI.extractIbanFromRib({ storedFileId });
          return result;
        },
        onAttachRib: async (formValues, storedFileId, bankAccountId) => {
          try {
            const result = await bankAccountsAPI.attachRibToBankAccount({
              compteBancaireId: bankAccountId,
              attachRibCommand: {
                storedFileId,
                iban: formValues.iban,
                bic: formValues.bic || undefined
              }
            });
            return {
              success: result.isSuccess ?? false,
              errorMessage: result.errorMessage
            };
          } catch (error) {
            return {
              success: false,
              errorMessage: 'Erreur lors de l\'attachement du RIB'
            };
          }
        },
        onSuccess: async () => {
          // After attaching RIB, select and refresh list
          onSelect(bankAccount.id);
          await fetchBankAccounts();
        }
      })
    });
  }, [fetchBankAccounts, onSelect, openDocumentProcess, personId]);

  // Handle creating new account from RIB
  const handleCreateNewAccount = useCallback(() => {
    openDocumentProcess({
      context: ContextEnum.Person,
      objectId: personId,
      config: createRibUploadConfig({
        personId,
        onExtractIban: async (storedFileId) => {
          const result = await documentsAPI.extractIbanFromRib({ storedFileId });
          return result;
        },
        onCreateBankAccount: async (formValues, storedFileId) => {
          try {
            const result = await bankAccountsAPI.createBankAccountFromRib({
              createBankAccountFromRibCommand: {
                personId,
                iban: formValues.iban,
                bic: formValues.bic || undefined,
                label: formValues.label,
                storedFileId
              }
            });

            if (result.isSuccess && result.id) {
              // After creating, select the new account
              onSelect(result.id);
            }

            return {
              success: result.isSuccess ?? false,
              errorMessage: result.errorMessage
            };
          } catch (error) {
            return {
              success: false,
              errorMessage: 'Erreur lors de la création du compte bancaire'
            };
          }
        },
        onSuccess: () => {
          fetchBankAccounts();
        }
      })
    });
  }, [fetchBankAccounts, onSelect, openDocumentProcess, personId]);

  const hasRib = useCallback((account: BankAccountWithStoredFile) => {
    return !!account.storedFileId;
  }, []);

  const isSelected = useCallback((accountId: string) => {
    return selectedBankAccountId === accountId;
  }, [selectedBankAccountId]);

  // View RIB document
  const handleViewRib = useCallback((
    e: React.MouseEvent,
    bankAccount: BankAccountWithStoredFile
  ) => {
    e.stopPropagation();
    if (bankAccount.storedFileId) {
      openDocumentViewer({
        title: `RIB - ${bankAccount.label}`,
        storedFileId: bankAccount.storedFileId
      });
    }
  }, [openDocumentViewer]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <GenericLoading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* List of existing bank accounts */}
      {bankAccounts.length > 0 ? (
        <div className="flex flex-col gap-3">
          {bankAccounts.map(bankAccount => (
            <div
              key={bankAccount.id}
              className={`group relative flex items-center gap-4 p-4 rounded-xl border-2 w-full cursor-pointer transition-all duration-200 ${isSelected(bankAccount.id)
                ? 'bg-green-50 border-green-500 shadow-md shadow-green-100'
                : 'bg-white border-gray-200 hover:border-primary/50 hover:shadow-sm'
                } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => handleSelectAccount(bankAccount)}
            >
              {/* Bank icon */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${isSelected(bankAccount.id)
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary'
                }`}>
                <Landmark className="w-6 h-6" />
              </div>

              {/* Account info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-bold tracking-wide ${isSelected(bankAccount.id) ? 'text-green-700' : 'text-gray-800'
                    }`}>
                    {bankAccount.iban.replace(/(.{4})/g, '$1 ').trim()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {hasRib(bankAccount) ? (
                    <button
                      type="button"
                      onClick={(e) => handleViewRib(e, bankAccount)}
                      title={t('CONTRACTUALIZATION.BANK_ACCOUNT.VIEW_RIB')}
                    >
                      <Badge
                        icon={<Eye className="w-3.5 h-3.5" />}
                        label={t('CONTRACTUALIZATION.BANK_ACCOUNT.VIEW_RIB')}
                        color={GenericColors.Primary}
                        badgeType="sm-bold"
                        className="cursor-pointer hover:ring-2"
                      />
                    </button>
                  ) : (
                    <Badge
                      label={t('CONTRACTUALIZATION.BANK_ACCOUNT.NO_RIB')}
                      color={GenericColors.Secondary}
                      badgeType="sm-bold"
                    />
                  )}
                  <p className="text-sm text-gray-500">{bankAccount.label}</p>
                </div>
              </div>

              {/* Selection indicator */}
              <div className="flex-shrink-0">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected(bankAccount.id)
                  ? 'bg-green-500 border-green-500'
                  : 'border-gray-300 group-hover:border-primary/50'
                  }`}>
                  {isSelected(bankAccount.id) && (
                    <CheckIcon className="w-4 h-4 text-white" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Landmark className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">
            {t('CONTRACTUALIZATION.BANK_ACCOUNT.NO_ACCOUNTS')}
          </p>
        </div>
      )}

      {/* Add new account button */}
      <div className="flex justify-center">
        <button
          className="w-full flex justify-center bg-white hover:bg-primary/5 text-primary gap-4 border-2 border-primary/20 hover:border-primary/50 rounded-md border-dashed p-2"
          onClick={handleCreateNewAccount}
          disabled={isSubmitting}
        >
          <LightAction
            icon={AddIcon}
            label={t('CONTRACTUALIZATION.BANK_ACCOUNT.ADD_NEW')}
          />
        </button>
      </div>
    </div>
  );
};
